import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import FormDrawer from "src/components/Form/FormDrawer";
import { dripiconlist } from "src/helpers/iconList";
import { getExtraServiceGroupListSaga } from "src/store/extra-service-group/saga";
import { ExtraServiceGroupDto } from "src/store/extra-service-group/type";
import { updateExtraServiceSaga } from "src/store/extra-service/saga";
import { ExtraServiceDetailDto } from "src/store/extra-service/type";
import { getPeriodTypes } from "src/store/system/saga";
import { PeriodTypeDto } from "src/store/system/type";
import { getTaxListSaga } from "src/store/tax/saga";
import { TaxDto } from "src/store/tax/type";
import * as Yup from "yup";

const ExtraServiceEditForm = ({
  data,
  onHide,
  open,
  onSuccessClick,
}: {
  data: ExtraServiceDetailDto;
  onSuccessClick: (message: string) => void;
  onHide: () => void;
  open: boolean;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [periods, setPeriods] = useState<PeriodTypeDto[]>([]);
  const [groups, setGroups] = useState<ExtraServiceGroupDto[]>([]);
  const [tax, setTax] = useState<TaxDto[]>([]);
  useEffect(() => {
    if (open) {
      dispatch(
        getTaxListSaga({
          payload: {
            onSuccess: (message, payload) => {
              setTax(payload);
            },
            onError: () => {},
          },
        })
      );
      dispatch(
        getExtraServiceGroupListSaga({
          payload: {
            onSuccess: (message, payload) => {
              setGroups(payload);
            },
            onError: () => {},
          },
        })
      );
      dispatch(
        getPeriodTypes({
          payload: {
            onSuccess: (message, payload) => {
              setPeriods(payload);
            },
            onError: () => {},
          },
        })
      );
    }
  }, [dispatch, open]);
  return (
    <React.Fragment>
      <FormDrawer
        drawerProps={{
          isOpen: open,
          onHide: onHide,
          title: t("ExtraService.EditFormTitle").toString(),
        }}
        onHide={onHide}
        loader
        initialValues={{
          Id: data.Id,
          ExtraServiceGroupId: data.ExtraServiceGroupId,
          PeriodTypeId: data.PeriodTypeId,
          Name: data.Name,
          Price: data.Price,
          Icon: data.Icon,
          Vat: data.Vat,
          ShowWeb: data.ShowWeb,
          PriceWithTax: data.PriceWithTax,
          TariffPricing: data.TariffPricing,
          Description: data.Description,
          Translates: data.Translates,
        }}
        onSubmit={(values) => {
          dispatch(
            updateExtraServiceSaga({
              payload: {
                onSuccess: (message, payload) => {
                  onHide();
                  onSuccessClick(message);
                },
                onError: (message) => {
                  toast.error(message, {
                    theme: "colored",
                  });
                },
              },
              id: data.Id,
              body: values,
            })
          );
        }}
        validationSchema={Yup.object().shape({
          Name: Yup.string().required(t("Validation.Required").toString()),
          Description: Yup.string().required(
            t("Validation.Required").toString()
          ),
          PeriodTypeId: Yup.number().required(
            t("Validation.Required").toString()
          ),
          Price: Yup.number().required(t("Validation.Required").toString()),
          ExtraServiceGroupId: Yup.string().required(
            t("Validation.Required").toString()
          ),
          Icon: Yup.string().required(t("Validation.Required").toString()),
          Vat: Yup.number().required(t("Validation.Required").toString()),
          Translates: Yup.array(
            Yup.object({
              Name: Yup.string().required(t("Validation.Required").toString()),
              Description: Yup.string().required(
                t("Validation.Required").toString()
              ),
            })
          ),
        })}
        multilanguageinputs={[
          {
            field: "Name",
            label: t("ExtraService.Name").toString(),
          },
          {
            field: "Description",
            label: t("ExtraService.Description").toString(),
            type: "textarea",
          },
        ]}
        inputs={[
          {
            field: "ExtraServiceGroupId",
            label: t("ExtraService.GroupName").toString(),
            type: "select",
            lookup: {
              labelKey: "Name",
              valueKey: "Id",
              options: groups,
            },
          },
          {
            field: "PeriodTypeId",
            label: t("ExtraService.PeriodType").toString(),
            placeholder: "",
            type: "select",
            lookup: {
              labelKey: "Name",
              valueKey: "EnumId",
              options: periods,
            },
          },
          {
            field: "Icon",
            label: t("ExtraService.Icon").toString(),
            type: "select",
            lookup: {
              renderValue: (e) => <i className={e} />,
              labelKey: "Id",
              valueKey: "Id",
              options: dripiconlist.map((x, i) => {
                return {
                  Id: x,
                  Name: x,
                };
              }),
            },
          },

          {
            field: "Vat",
            col: 6,
            label: t("ExtraService.Tax").toString(),
            type: "select",
            lookup: {
              labelKey: "Value",
              valueKey: "Value",
              options: tax,
            },
          },
          {
            field: "Price",
            label: t("ExtraService.Price").toString(),
            col: 6,
            type: "number",
          },
          {
            field: "PriceWithTax",
            label: t("ExtraService.PriceWithTax").toString(),
            type: "switch",
          },
        ]}
      />
    </React.Fragment>
  );
};
export default ExtraServiceEditForm;
